import React from 'react';
import {
  Box, Container, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { C, Section, SectionLabel } from './theme';

const cols = [
  { key: 'sm', label: 'Premium Backlink Services', highlight: true },
  { key: 'mv', label: 'Fiverr / Marketplace Sellers', highlight: false },
  { key: 'sh', label: 'Automated Link Tools', highlight: false },
  { key: 'ds', label: 'Traditional SEO Agency', highlight: false },
];

const rows = [
  {
    label: 'Link source',
    sm: 'Real, manually vetted websites with organic traffic',
    mv: 'Often unknown or reused across many clients',
    sh: 'Automated directories & low-quality networks',
    ds: 'Usually outsourced, quality varies',
  },
  {
    label: 'Authority (DA/DR)',
    sm: 'Verified DA/DR 30–90+ before every placement',
    mv: 'Rarely verified, often inflated',
    sh: 'Low authority, bulk-generated sites',
    ds: 'Varies widely by agency',
  },
  {
    label: 'Safety',
    sm: '100% white-hat, Google guideline compliant',
    mv: 'Risk of PBNs or link schemes',
    sh: 'High penalty risk (spam signals)',
    ds: 'Usually safe, but slower turnaround',
  },
  {
    label: 'Transparency',
    sm: 'Full report with live URLs for every link',
    mv: 'Limited or no reporting',
    sh: 'Automated logs only, no manual review',
    ds: 'Reports often delayed or vague',
  },
  {
    label: 'Best for',
    sm: 'Sustainable, long-term ranking growth',
    mv: 'Quick, low-budget one-off orders',
    sh: 'Nothing recommended — high risk',
    ds: 'Full-service marketing, higher cost',
  },
];

const VpsComparison = () => (
  <Section id="comparison" sx={{ background: C.bgSection, borderBottom: `1px solid ${C.border}` }}>
    <Container maxWidth="lg">

      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 5 }}>
        <SectionLabel>How We Compare</SectionLabel>
        <Typography variant="h2" sx={{
          fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.4rem' },
          color: C.textPrimary, letterSpacing: '-0.03em', mb: 1.5,
        }}>
          Why serious marketers skip the shortcuts
        </Typography>
        <Typography sx={{ color: C.textSecondary, fontSize: '1rem', maxWidth: 580, mx: 'auto', lineHeight: 1.6 }}>
          Not all backlinks are equal. See how our manual, white-hat process compares
          to marketplace sellers, automated tools, and traditional agencies.
        </Typography>
      </Box>

      {/* Comparison Table */}
      <TableContainer component={Paper} elevation={0} sx={{
        border: `1px solid ${C.border}`,
        borderRadius: '16px',
        overflow: 'hidden',
        background: '#FFFFFF',
      }}>
        <Table>
          <TableHead>
            <TableRow sx={{ background: '#F1F5F9' }}>
              <TableCell sx={{
                fontWeight: 700, fontSize: '0.82rem', color: C.textMuted,
                borderBottom: `1px solid ${C.border}`, width: '18%', py: 2.5, px: 3,
              }}>
                Feature
              </TableCell>
              {cols.map((col) => (
                <TableCell key={col.key} align="center" sx={{
                  borderBottom: `1px solid ${C.border}`, py: 2.5, px: 2,
                  background: col.highlight ? C.primaryLight : 'transparent',
                }}>
                  <Typography sx={{
                    fontWeight: col.highlight ? 800 : 700,
                    fontSize: '0.9rem',
                    color: col.highlight ? C.primary : C.textPrimary,
                  }}>
                    {col.highlight && '⚡ '}{col.label}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={row.label} sx={{
                background: i % 2 === 0 ? '#FFFFFF' : '#FAFAFA',
                '&:last-child td': { borderBottom: 0 },
              }}>
                <TableCell sx={{
                  fontWeight: 700, fontSize: '0.85rem', color: C.textPrimary,
                  borderBottom: `1px solid ${C.border}`, py: 2, px: 3,
                }}>
                  {row.label}
                </TableCell>
                {cols.map((col) => (
                  <TableCell key={col.key} align="center" sx={{
                    fontSize: '0.84rem',
                    color: col.highlight ? C.textPrimary : C.textSecondary,
                    fontWeight: col.highlight ? 600 : 400,
                    borderBottom: `1px solid ${C.border}`,
                    background: col.highlight ? `${C.primaryLight}50` : 'transparent',
                    py: 2, px: 2,
                  }}>
                    {col.highlight && (
                      <CheckCircle sx={{ fontSize: 15, color: C.success, mr: 0.75, verticalAlign: 'middle' }} />
                    )}
                    {row[col.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

    </Container>
  </Section>
);

export default VpsComparison;
